<template>
  <div v-if="selectedItem" class="ui-dropdown">
    <b-dropdown
      :expanded="expanded"
      aria-role="menu"
      position="is-bottom-left"
      class="ui-dropdown__container"
      :style="dropdownStyle"
    >
      <template #trigger="{ active }">
        <b-button
          :expanded="expanded"
          class="ui-dropdown__btn"
          :class="[{ 'ui-dropdown__btn--specific': isSpecific }, { 'ui-dropdown__btn--expanded': expanded }]"
        >
          <span v-if="selectedItem.icon" class="icon is-small mr-2">
            <i class="fas" :class="`fa-${selectedItem.icon}`"></i>
          </span>
          <span class="has-text-weight-medium ui-dropdown__label" :style="labelStyle">
            {{ selectedItem.label }}
          </span>
          <span
            class="icon is-small ml-3 has-text-link ui-dropdown__arrow"
            :class="{ 'ui-dropdown__arrow--specific': isSpecific }"
          >
            <i class="fas" :class="active ? 'fa-chevron-up' : 'fa-chevron-down'" />
          </span>
        </b-button>
      </template>
      <b-dropdown-item
        v-for="(category, i) in groupedItems"
        :key="i"
        class="has-text-left ui-dropdown__group-items"
        aria-role="menuitem"
        custom
      >
        <span class="ui-dropdown__group-title is-block mb-4px is-size-7">{{ category.label }}</span>
        <div
          v-for="(item, index) in category.items"
          :key="index"
          class="ui-dropdown__sub-item ml-8px pb-8px"
          :class="{
            'ui-dropdown__sub-item--current': item.value === selectedItem.value,
            'ui-dropdown__sub-item--specific': isSpecific
          }"
          @click="onSelectItem(item.value)"
        >
          <span v-if="item.value === selectedItem.value" class="icon is-small mr-1"><i class="fas fa-check" /></span>
          <span class="is-size-6">{{ item.label }}</span>
        </div>
      </b-dropdown-item>
      <b-dropdown-item
        v-for="(item, index) in items"
        :key="index"
        :value="item.value"
        class="ui-dropdown__item"
        :class="{
          'ui-dropdown__item--current': item.value === selectedItem.value,
          'ui-dropdown__item--specific': isSpecific
        }"
        has-link
        aria-role="menuitem"
        @click="onSelectItem(item.value)"
      >
        <span v-if="item.value === selectedItem.value" class="icon is-small mr-1"><i class="fas fa-check" /></span>
        {{ item.label }}
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
export default {
  props: {
    value: String,
    groupedItems: {
      type: Array,
      default() {
        return []
      }
    },
    items: {
      type: Array,
      default() {
        return []
      }
    },
    isSpecific: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: false
    },
    dropdownWidth: {
      type: Number,
      default: null
    }
  },
  computed: {
    selectedItem() {
      if (!this.items || this.items.length < 1) {
        // Logique pour des items groupés
        if (!this.groupedItems || this.groupedItems.length < 1) {
          return null
        }

        let selectedItem = ''
        for (const category of this.groupedItems) {
          selectedItem = category.items.find((x) => x.value.projectId == this.value)
          if (selectedItem) break
        }
        if (!selectedItem) {
          return this.groupedItems[1].items[0]
        }
        return selectedItem
      }

      // Logique pour des items non-groupés
      const selectedItem = this.items.find((x) => x.value == this.value)
      if (!selectedItem) return this.items[0]
      return selectedItem
    },
    dropdownStyle() {
      if (!this.dropdownWidth) return
      return `width: ${this.dropdownWidth}px`
    },
    labelStyle() {
      if (!this.dropdownWidth) return `width: calc(100% - 70px)`
      return `width: ${this.dropdownWidth - 70}px` // Allow space for arrow
    }
  },
  methods: {
    onSelectItem(value) {
      this.$emit('input', value)
    }
  }
}
</script>

<style lang="scss">
.ui-dropdown {
  &__container {
    .dropdown-content {
      background-color: $grey-dark;
    }

    &.is-expanded .dropdown-menu {
      min-width: 260px;
    }
  }

  &__btn {
    background: $grey-lightest;
    border-color: $grey-lightest;
    color: $primary;

    &:hover,
    &:focus {
      background: $grey-lightest;
      border-color: $grey-lighter;
      color: $primary;
    }

    &--specific {
      color: $secondary;

      &:hover,
      &:focus {
        color: $secondary;
      }
    }

    &--expanded {
      justify-content: flex-start;
      flex-grow: 1;
      width: 100%;

      // Hack: align-items inside span generated by b-button
      & > span {
        display: flex;
        justify-content: flex-start;
        flex-grow: 1;
        width: 100%;
      }
    }
  }

  &__label {
    flex-grow: 1;
    text-align: left;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__arrow {
    color: $blue !important;

    &--specific {
      color: $turquoise !important;
    }
  }

  &__item {
    color: $white;
    opacity: 0.7;
    padding: $space-4px $space-16px;
    cursor: pointer;
    transition: opacity 0.1s ease-in-out;

    &:hover,
    &:focus {
      opacity: 1;
    }

    &--current {
      opacity: 1;
      cursor: default;
      pointer-events: none;
    }
  }

  &__group-items {
    padding: $space-4px $space-16px;
  }

  &__group-title {
    color: $white;
    opacity: 0.7;
  }

  &__sub-item {
    color: $white;
    opacity: 0.7;
    cursor: pointer;
    line-height: 1.3;
    transition: opacity 0.1s ease-in-out;

    &:hover,
    &:focus {
      opacity: 1;
    }

    &--current {
      opacity: 1;
      cursor: default;
      pointer-events: none;
    }
  }
}

@include tablet {
  .ui-dropdown {
    &__container {
      margin-left: auto;
      margin-right: 0;
    }
  }
}
</style>
